import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { UserContext } from '../../Context/UserContext';
import { FaArrowLeft, FaChevronDown, FaCheckCircle } from 'react-icons/fa';
import './MenusPage.css';

const getImageUrl = (baseUrl, img) => {
    if (!img) return "data:image/svg+xml;charset=UTF-8,%3Csvg%20width%3D%22100%22%20height%3D%22100%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Crect%20width%3D%22100%25%22%20height%3D%22100%25%22%20fill%3D%22%23eee%22%2F%3E%3Ctext%20x%3D%2250%25%22%20y%3D%2250%25%22%20font-family%3D%22Arial%22%20font-size%3D%2212%22%20fill%3D%22%23aaa%22%20dominant-baseline%3D%22middle%22%20text-anchor%3D%22middle%22%3ENo%20Image%3C%2Ftext%3E%3C%2Fsvg%3E";
    if (img.startsWith('http')) return img;
    if (img.startsWith('/')) return `${baseUrl || ''}${img}`;
    return `${baseUrl || ''}/api/images/${img}`;
};

function MenusPage() {
    const { baseUrl, getHeaders, configLoading } = useContext(UserContext);
    const navigate = useNavigate();
    
    // menuTimings will store grouped timing objects
    const [menuTimings, setMenuTimings] = useState([]);
    const [loading, setLoading] = useState(true);

    const [currentIndex, setCurrentIndex] = useState(0);
    const [progress, setProgress] = useState(0);
    const [isPaused, setIsPaused] = useState(false);
    const [slideKey, setSlideKey] = useState(0);
    
    // Configurations
    const [animationType, setAnimationType] = useState('zoom');
    const [slideDurationSec, setSlideDurationSec] = useState(10);
    const [rows, setRows] = useState(1);
    const [columns, setColumns] = useState(1);
    const [animSpeed, setAnimSpeed] = useState('0.8s');
    const [cardStyle, setCardStyle] = useState('style-card');
    const [theme, setTheme] = useState('theme-light'); 
    const [showHeader, setShowHeader] = useState(true);
    const [itemAnimType, setItemAnimType] = useState('fade'); // New state for staggered item animation
    const [selectedMenu, setSelectedMenu] = useState('All');
    const [showItemDetails, setShowItemDetails] = useState('none');

    useEffect(() => {
        const handleKeyDown = (e) => {
            const isInput = e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable;
            if (!isInput && !e.ctrlKey && !e.altKey && !e.metaKey && e.key.toLowerCase() === 'h') {
                e.preventDefault();
                setShowHeader(prev => !prev);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    useEffect(() => {
        if (configLoading) return;

        const fetchData = async () => {
            try {
                const now = new Date();
                const clientTime = now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }); // 24-hour HH:mm format
                const clientDay = now.toLocaleDateString('en-US', { weekday: 'long' });
                // Handle timezone offset for YYYY-MM-DD to avoid date shifting
                const tzOffset = now.getTimezoneOffset() * 60000;
                const localISOTime = (new Date(Date.now() - tzOffset)).toISOString().slice(0, -1);
                const clientDate = localISOTime.split('T')[0];
                
                const [timingsRes, itemsRes] = await Promise.all([
                    axios.get(`${baseUrl || ''}/api/menu_timings/active?time=${clientTime}&day=${clientDay}&date=${clientDate}`, { headers: getHeaders() }),
                    axios.get(`${baseUrl || ''}/api/items`, { headers: getHeaders() })
                ]);
                
                const activeTimings = timingsRes.data || [];
                const allItems = itemsRes.data || [];
                
                const groupedTimings = [];

                activeTimings.forEach(timing => {
                    const timingName = timing.timing_name || 'Active Menu';
                    const itemsInTiming = timing.items || [];
                    const timingItemIds = itemsInTiming.map(it => 
                        typeof it === 'object' ? (it._id || it.id || '').toString() : it.toString()
                    );
                    
                    const matchingItems = allItems.filter(item => {
                        const itemId = (item._id || item.id || '').toString();
                        return timingItemIds.includes(itemId);
                    });

                    if (matchingItems.length > 0) {
                        groupedTimings.push({
                            _id: timing._id || Math.random().toString(),
                            timing_name: timingName.toUpperCase(),
                            items: matchingItems
                        });
                    }
                });

                setMenuTimings(groupedTimings);
            } catch (err) {
                console.error("Error fetching menu items:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [baseUrl, configLoading, getHeaders]);

    const maxItems = rows * columns;

    const presentationSlides = React.useMemo(() => {
        const slides = [];
        const filteredTimings = selectedMenu === 'All' 
            ? menuTimings 
            : menuTimings.filter(t => t.timing_name === selectedMenu);

        filteredTimings.forEach(timing => {
            const items = timing.items || [];
            if (items.length === 0) {
                slides.push({ ...timing, items: [] });
            } else {
                for (let i = 0; i < items.length; i += maxItems) {
                    slides.push({
                        ...timing,
                        _id: timing._id + '_' + i,
                        items: items.slice(i, i + maxItems)
                    });
                }
            }
        });
        return slides;
    }, [menuTimings, maxItems, selectedMenu]);

    // Timer Logic
    useEffect(() => {
        if (isPaused || presentationSlides.length === 0) return;

        const intervalTime = 50;
        const step = (intervalTime / (slideDurationSec * 1000)) * 100;

        const timer = setInterval(() => {
            setProgress((prev) => {
                if (prev >= 100) {
                    setCurrentIndex((prevIndex) => (prevIndex + 1) % presentationSlides.length);
                    setSlideKey(k => k + 1);
                    return 0;
                }
                return prev + step;
            });
        }, intervalTime);

        return () => clearInterval(timer);
    }, [presentationSlides.length, isPaused, slideDurationSec]);

    // Handle Layout Change
    useEffect(() => {
        setProgress(0);
        setCurrentIndex(0);
        setSlideKey(k => k + 1);
    }, [rows, columns, menuTimings, selectedMenu]);


    if (loading) {
        return (
            <div className={`digital-menu-board ${theme}`}>
                <div className="spinner-border" style={{color: 'var(--accent-color)'}} role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    const visibleTimings = presentationSlides.slice(currentIndex, currentIndex + 1);

    return (
        <div className={`digital-menu-board ${theme !== 'theme-light' ? theme : ''} ${cardStyle}`} style={{ '--anim-duration': animSpeed }}>
            {showHeader && (
                <header className="board-header">
                <div className="header-left">
                    <button onClick={() => navigate(-1)} className="board-back-btn">
                        <FaArrowLeft /> Back
                    </button>
                    <h1 className="board-title">ACTIVE MENUS</h1>
                </div>

                <div className="header-controls">
                    {/* Menu Category Filter */}
                    <div className="control-dropdown-wrapper">
                        <select 
                            value={selectedMenu} 
                            onChange={(e) => setSelectedMenu(e.target.value)}
                            className="control-select"
                            title="Filter Menus"
                        >
                            <option value="All">All Menus</option>
                            {menuTimings.map(t => (
                                <option key={t._id} value={t.timing_name}>{t.timing_name}</option>
                            ))}
                        </select>
                        <FaChevronDown className="control-icon" />
                    </div>

                    {/* Card Style Enable/Disable */}
                    <div className="control-dropdown-wrapper">
                        <select 
                            value={cardStyle} 
                            onChange={(e) => setCardStyle(e.target.value)}
                            className="control-select"
                        >
                            <option value="style-card">Card Style</option>
                            <option value="style-no-card">No Card</option>
                        </select>
                        <FaChevronDown className="control-icon" />
                    </div>

                    {/* Theme Dropdown */}
                    <div className="control-dropdown-wrapper">
                        <select 
                            value={theme} 
                            onChange={(e) => setTheme(e.target.value)}
                            className="control-select"
                        >
                            <option value="theme-light">Light Classic</option>
                            <option value="theme-sunrise">Warm Sunrise</option>
                            <option value="theme-mint">Mint Fresh</option>
                            <option value="theme-lavender">Soft Lavender</option>
                        </select>
                        <FaChevronDown className="control-icon" />
                    </div>

                    {/* Layout Inputs */}
                    <div className="control-group">
                        <span className="control-label">Rows:</span>
                        <input 
                            type="number" min="1" max="10" value={rows} 
                            onChange={(e) => setRows(Math.max(1, Number(e.target.value)) || 1)}
                            className="control-input-num"
                        />
                    </div>
                    <div className="control-group">
                        <span className="control-label">Cols:</span>
                        <input 
                            type="number" min="1" max="10" value={columns} 
                            onChange={(e) => setColumns(Math.max(1, Number(e.target.value)) || 1)}
                            className="control-input-num"
                        />
                    </div>
                    <div className="control-group">
                        <span className="control-label">Time(s):</span>
                        <input 
                            type="number" min="1" max="60" value={slideDurationSec} 
                            onChange={(e) => setSlideDurationSec(Math.max(1, Number(e.target.value)) || 10)}
                            className="control-input-num"
                        />
                    </div>
                    
                    {/* Animation Speed Dropdown */}
                    <div className="control-dropdown-wrapper">
                        <select 
                            value={animSpeed} 
                            onChange={(e) => {
                                setAnimSpeed(e.target.value);
                                setSlideKey(k => k + 1);
                            }}
                            className="control-select"
                        >
                            <option value="0.5s">Fast</option>
                            <option value="0.8s">Normal</option>
                            <option value="1.5s">Slow</option>
                        </select>
                        <FaChevronDown className="control-icon" />
                    </div>

                    {/* Animation Type Dropdown */}
                    <div className="control-dropdown-wrapper">
                        <select 
                            value={animationType} 
                            onChange={(e) => {
                                setAnimationType(e.target.value);
                                setSlideKey(k => k + 1);
                            }}
                            className="control-select"
                            title="Card Animation"
                        >
                            <option value="none">Card Anim: None</option>
                            <option value="zoom">Card: Zoom</option>
                            <option value="fade">Card: Fade In</option>
                            <option value="slide-up">Card: Slide Up</option>
                            <option value="flip">Card: 3D Flip</option>
                        </select>
                        <FaChevronDown className="control-icon" />
                    </div>

                    {/* Item Details Dropdown */}
                    <div className="control-dropdown-wrapper">
                        <select 
                            value={showItemDetails} 
                            onChange={(e) => setShowItemDetails(e.target.value)}
                            className="control-select"
                            title="Item Details"
                        >
                            <option value="none">Details: Hidden</option>
                            <option value="ingredients">Show Ingredients</option>
                            <option value="variants">Show Variants</option>
                            <option value="all">Show All Details</option>
                        </select>
                        <FaChevronDown className="control-icon" />
                    </div>

                    {/* Item Animation Dropdown */}
                    <div className="control-dropdown-wrapper">
                        <select 
                            value={itemAnimType} 
                            onChange={(e) => {
                                setItemAnimType(e.target.value);
                                setSlideKey(k => k + 1);
                            }}
                            className="control-select"
                            title="Item Animation"
                        >
                            <option value="none">Items Anim: None</option>
                            <option value="zoom">Items: Zoom</option>
                            <option value="fade">Items: Fade In</option>
                            <option value="slide-up">Items: Slide Up</option>
                            <option value="flip">Items: 3D Flip</option>
                            <option value="drop-bounce">Items: Drop from Top</option>
                        </select>
                        <FaChevronDown className="control-icon" />
                    </div>
                </div>
            </header>
            )}

            <main 
                key={slideKey}
                className="board-grid" 
                style={{ 
                    gridTemplateColumns: '1fr',
                    gridTemplateRows: '1fr' 
                }}
                onMouseEnter={() => setIsPaused(true)} 
                onMouseLeave={() => setIsPaused(false)}
            >
                {visibleTimings.length > 0 ? (
                    visibleTimings.map((slide, idx) => {
                        const displayItems = slide.items;

                        return (
                            <div key={slide._id + '_' + currentIndex} className={`timing-wrapper anim-${animationType}`}>
                                <h2 className="timing-heading-center">{slide.timing_name}</h2>
                                <div className="board-combo-card">
                                    <div className="board-combo-content">
                                        <div className="compact-image-container">
                                            <div 
                                                className={`multi-image-grid grid-cols-${columns}`}
                                                style={{
                                                    gridTemplateColumns: `repeat(${columns}, 1fr)`,
                                                    gridTemplateRows: `repeat(${rows}, 1fr)`
                                                }}
                                            >
                                            {displayItems.map((item, i) => {
                                                const baseDelay = parseFloat(animSpeed) || 0.8;
                                                const staggerDelay = 0.4;
                                                const animDelay = `${baseDelay + (i * staggerDelay)}s`;
                                                return (
                                                <div 
                                                    key={i} 
                                                    className={`menu-item-cell ${itemAnimType !== 'none' ? `anim-${itemAnimType}` : ''}`}
                                                    style={itemAnimType !== 'none' ? { animationDelay: animDelay } : {}}
                                                >
                                                    <img 
                                                        src={getImageUrl(baseUrl, item.image || item.image_url)} 
                                                        alt={item.item_name} 
                                                        className="compact-image mix-blend-multiply"
                                                        style={{ mixBlendMode: 'multiply' }}
                                                        onError={(e) => { e.target.src = getImageUrl(baseUrl, null); }}
                                                    />
                                                    <div className="item-info-right">
                                                        <div className="item-name-below">
                                                            <FaCheckCircle className="check-icon-small" />
                                                            <span>{item.item_name}</span>
                                                        </div>
                                                        
                                                        {showItemDetails !== 'none' && (
                                                        <div className="item-details-container">
                                                            {(showItemDetails === 'ingredients' || showItemDetails === 'all') && (
                                                                (() => {
                                                                    let ings = item.ingredients;
                                                                    if (typeof ings === 'string') {
                                                                        try { ings = JSON.parse(ings); } catch(e) {}
                                                                    }
                                                                    if (!Array.isArray(ings) || ings.length === 0) return null;

                                                                    return (
                                                                        <div className="detail-section">
                                                                            <div className="detail-title">Ingredients</div>
                                                                            <div className="detail-list">
                                                                                {ings.map((ing, ingIdx) => {
                                                                                    const ingName = typeof ing === 'object' ? ing.ingredients_name || ing.ingredient_name || ing.name || ing.item_name : ing;
                                                                                    if (!ingName) return null;
                                                                                    return (
                                                                                        <span key={ingIdx} className="detail-badge ingredient-badge">
                                                                                            {ingName}
                                                                                        </span>
                                                                                    );
                                                                                })}
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })()
                                                            )}
                                                            
                                                            {(showItemDetails === 'variants' || showItemDetails === 'all') && (
                                                                (() => {
                                                                    let allVariants = [];
                                                                    
                                                                    let pVars = item.variants;
                                                                    if (typeof pVars === 'string') {
                                                                        try { pVars = JSON.parse(pVars); } catch(e) {}
                                                                    }
                                                                    
                                                                    if (Array.isArray(pVars)) {
                                                                        allVariants.push(...pVars.map(v => typeof v === 'object' ? v.variant_name || v.name : v));
                                                                    } else if (pVars && typeof pVars === 'object') {
                                                                        if (pVars.size?.enabled) {
                                                                            if (pVars.size.small_price !== undefined && pVars.size.small_price !== '') allVariants.push('Small');
                                                                            if (pVars.size.medium_price !== undefined && pVars.size.medium_price !== '') allVariants.push('Medium');
                                                                            if (pVars.size.large_price !== undefined && pVars.size.large_price !== '') allVariants.push('Large');
                                                                        }
                                                                        if (pVars.cold?.enabled) {
                                                                            allVariants.push(pVars.cold.ice_preference === 'with_ice' ? 'With Ice' : 'Without Ice');
                                                                        }
                                                                        if (pVars.spicy?.enabled) {
                                                                            if (pVars.spicy.spicy_price !== undefined) allVariants.push('Spicy');
                                                                            if (pVars.spicy.non_spicy_price !== undefined) allVariants.push('Non-Spicy');
                                                                        }
                                                                        if (pVars.sugar?.enabled) {
                                                                            allVariants.push(`${pVars.sugar.level || 'Custom'} Sugar`);
                                                                        }
                                                                    }
                                                                    
                                                                    // Fallback for root-level predefined variants
                                                                    const checkRootVariant = (rootProp) => {
                                                                        let prop = item[rootProp];
                                                                        if (typeof prop === 'string') {
                                                                            try { prop = JSON.parse(prop); } catch(e) {}
                                                                        }
                                                                        return prop;
                                                                    };

                                                                    const rSize = checkRootVariant('size');
                                                                    if (rSize && typeof rSize === 'object' && rSize.enabled) {
                                                                        if (rSize.small_price !== undefined && rSize.small_price !== '') allVariants.push('Small');
                                                                        if (rSize.medium_price !== undefined && rSize.medium_price !== '') allVariants.push('Medium');
                                                                        if (rSize.large_price !== undefined && rSize.large_price !== '') allVariants.push('Large');
                                                                    }
                                                                    
                                                                    const rCold = checkRootVariant('cold');
                                                                    if (rCold && typeof rCold === 'object' && rCold.enabled) {
                                                                        allVariants.push(rCold.ice_preference === 'with_ice' ? 'With Ice' : 'Without Ice');
                                                                    }

                                                                    const rSpicy = checkRootVariant('spicy');
                                                                    if (rSpicy && typeof rSpicy === 'object' && rSpicy.enabled) {
                                                                        if (rSpicy.spicy_price !== undefined && rSpicy.spicy_price !== '') allVariants.push('Spicy');
                                                                        if (rSpicy.non_spicy_price !== undefined && rSpicy.non_spicy_price !== '') allVariants.push('Non-Spicy');
                                                                    }

                                                                    const rSugar = checkRootVariant('sugar');
                                                                    if (rSugar && typeof rSugar === 'object' && rSugar.enabled) {
                                                                        allVariants.push(`${rSugar.level || 'Custom'} Sugar`);
                                                                    }
                                                                    
                                                                    let cVars = item.custom_variants;
                                                                    if (typeof cVars === 'string') {
                                                                        try { cVars = JSON.parse(cVars); } catch(e) {}
                                                                    }

                                                                    if (Array.isArray(cVars)) {
                                                                        cVars.forEach(cv => {
                                                                            if (cv.enabled) allVariants.push(cv.heading || cv.name);
                                                                        });
                                                                    } else if (cVars && typeof cVars === 'object') {
                                                                        allVariants.push(...Object.entries(cVars).map(([k, v]) => v.heading || v.name || k));
                                                                    }

                                                                    allVariants = [...new Set(allVariants.filter(Boolean))];

                                                                    if (allVariants.length === 0) return null;

                                                                    return (
                                                                        <div className="detail-section">
                                                                            <div className="detail-title">Variants</div>
                                                                            <div className="detail-list">
                                                                                {allVariants.map((v, vIdx) => (
                                                                                    <span key={vIdx} className="detail-badge variant-badge">
                                                                                        {typeof v === 'string' ? v.charAt(0).toUpperCase() + v.slice(1) : v}
                                                                                    </span>
                                                                                ))}
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })()
                                                            )}
                                                        </div>
                                                    )}
                                                    </div>
                                                </div>
                                                );
                                            })}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })
                ) : (
                    <div className="board-empty-state">
                        <h2>No Active Menus Available</h2>
                        <p>There are no items attached to any currently active menu timings.</p>
                    </div>
                )}
            </main>

            {/* Footer Progress Bar */}
            {presentationSlides.length > 0 && showHeader && (
                <div className="board-footer-timer">
                    <div className="board-timer-text">
                        <span>Showing {Math.min(currentIndex + 1, presentationSlides.length)} of {presentationSlides.length} menus</span>
                        <span>{Math.max(0, Math.ceil(((slideDurationSec * 1000) - (progress / 100 * (slideDurationSec * 1000))) / 1000))}s</span>
                    </div>
                    <div className="board-progress-bar">
                        <div className="board-progress-fill" style={{ width: `${progress}%` }}></div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default MenusPage;
